/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectGdprConfigRequired } from '../../redux/features/configuration/configuration.slice';

import useProcedure from '../../procedure/useProcedure';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';

export default function useEPCTCreationDialog(procedureId) {
  const { t } = useTranslation();
  const { control, handleSubmit, errors, watch, formState, setValue } = useForm({ mode: 'onChange' });
  const RO = watch('RO');
  const procedure = useProcedure(procedureId);
  const [languageList, setLanguageList] = useState([]);

  const isGdprRequired = useSelector(state => selectGdprConfigRequired(state, procedure.id));

  const ePCTLanguageListConfiguration = useConfiguration(
    ConfigResourceProps.RECEIVING_OFFICES_LANGUAGE_CONFIGURATION,
    procedure.id,
  );
  const ePCTLanguageList = ePCTLanguageListConfiguration.resource?.receivingOfficesLanguages;

  const ePCTReceivingOfficesConfiguration = useConfiguration(
    ConfigResourceProps.RECEIVING_OFFICES_CONFIGURATION,
    procedure.id,
  );

  const ePCTReceivingOffices = ePCTReceivingOfficesConfiguration.resource?.receivingOffices;

  const ROList = ePCTReceivingOffices?.map(receivingOffice => ({
    ...receivingOffice,
    itemLabel: t(receivingOffice.itemLabel),
  }));

  const defaultOffice = ePCTReceivingOffices?.find(ro => ro.default)?.itemId;

  const userReferenceRules = {
    required: t('user.reference.required'),
    maxLength: { value: 23, message: t('submission.epct.createDialog.maxLength.pattern') },
    pattern: {
      value: /^[\da-zA-Z-]*$/,
      message: t('submission.epct.createDialog.fileReference.pattern'),
    },
  };

  useEffect(() => {
    const officeLanguages = ePCTLanguageList
      ?.filter(language => RO && language.type.toLowerCase() === RO?.toLowerCase())
      .map(language => ({ ...language, itemLabel: t(language.itemLabel) }));
    setLanguageList(officeLanguages);

    const defaultLanguage = officeLanguages?.find(language => language.default)?.itemId;
    setValue('requestLanguage', defaultLanguage, { shouldValidate: true });
  }, [RO]);

  return {
    ROList,
    defaultOffice,
    languageList,
    isGdprRequired,
    userReferenceRules,
    RO,
    control,
    handleSubmit,
    errors,
    formState,
    t,
  };
}
